import { Component, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpService } from 'src/app/services/http.service';
import { environment } from 'src/environments/environment';

interface PlatformNotificationSettings {
  activationAlertsEnabled: boolean;
  premiumActivationRecipients: string[];
  royalActivationRecipients: string[];
  digestEnabled: boolean;
  digestRecipients: string[];
  digestAiSummaryEnabled: boolean;
}

@Component({
  selector: 'app-platform-settings',
  templateUrl: './platform-settings.component.html',
  styleUrls: ['./platform-settings.component.scss'],
})
export class PlatformSettingsComponent implements OnInit {
  private readonly apiUrl = environment.apiUrl;
  private readonly settingsUrl = `${this.apiUrl}/admin-center/platform-settings/v1`;
  private readonly digestUrl = `${this.apiUrl}/admin-center/platform-digest/v1`;

  isLoading = false;
  isSaving = false;
  isDigestActionRunning = false;

  activationAlertsEnabled = false;
  premiumRecipientsText = '';
  royalRecipientsText = '';
  digestEnabled = false;
  digestRecipientsText = '';
  digestAiSummaryEnabled = false;

  digestPreview: any = null;
  isPreviewOpen = false;

  constructor(
    private httpService: HttpService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadSettings();
  }

  async loadSettings() {
    this.isLoading = true;
    try {
      const settings: PlatformNotificationSettings = await this.httpService
        .doGet(this.settingsUrl)
        .toPromise();
      this.activationAlertsEnabled = !!settings?.activationAlertsEnabled;
      this.premiumRecipientsText = (settings?.premiumActivationRecipients || []).join('\n');
      this.royalRecipientsText = (settings?.royalActivationRecipients || []).join('\n');
      this.digestEnabled = !!settings?.digestEnabled;
      this.digestRecipientsText = (settings?.digestRecipients || []).join('\n');
      this.digestAiSummaryEnabled = !!settings?.digestAiSummaryEnabled;
    } catch (error: any) {
      console.log('Error loading platform settings: ', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.message || 'Could not load platform settings',
      });
    } finally {
      this.isLoading = false;
    }
  }

  private parseRecipients(text: string): string[] {
    return (text || '')
      .split(/[\n,;]+/)
      .map((value) => value.trim())
      .filter((value) => !!value);
  }

  async save() {
    this.isSaving = true;
    const payload = {
      activationAlertsEnabled: this.activationAlertsEnabled,
      premiumActivationRecipients: this.parseRecipients(this.premiumRecipientsText),
      royalActivationRecipients: this.parseRecipients(this.royalRecipientsText),
      digestEnabled: this.digestEnabled,
      digestRecipients: this.parseRecipients(this.digestRecipientsText),
      digestAiSummaryEnabled: this.digestAiSummaryEnabled,
    };
    try {
      const settings: PlatformNotificationSettings = await this.httpService
        .doPut(this.settingsUrl, payload)
        .toPromise();
      this.premiumRecipientsText = (settings?.premiumActivationRecipients || []).join('\n');
      this.royalRecipientsText = (settings?.royalActivationRecipients || []).join('\n');
      this.digestRecipientsText = (settings?.digestRecipients || []).join('\n');
      this.messageService.add({
        severity: 'success',
        summary: 'Successful',
        detail: 'Platform settings saved',
        life: 3000,
      });
    } catch (error: any) {
      console.log('Error saving platform settings: ', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.message || 'Could not save platform settings',
      });
    } finally {
      this.isSaving = false;
    }
  }

  async previewDigest() {
    this.isDigestActionRunning = true;
    try {
      this.digestPreview = await this.httpService
        .doGet(`${this.digestUrl}/preview`)
        .toPromise();
      this.isPreviewOpen = true;
    } catch (error: any) {
      console.log('Error previewing digest: ', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.message || 'Could not load digest preview',
      });
    } finally {
      this.isDigestActionRunning = false;
    }
  }

  confirmSendTestDigest() {
    this.confirmationService.confirm({
      message: 'Send a test digest email to the configured recipients now?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.sendTestDigest();
      },
    });
  }

  private async sendTestDigest() {
    this.isDigestActionRunning = true;
    try {
      await this.httpService.doPost(`${this.digestUrl}/send-test`, {}).toPromise();
      this.messageService.add({
        severity: 'success',
        summary: 'Successful',
        detail: 'Test digest sent to configured recipients',
        life: 3000,
      });
    } catch (error: any) {
      console.log('Error sending test digest: ', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.message || 'Could not send test digest',
      });
    } finally {
      this.isDigestActionRunning = false;
    }
  }
}
